import React from "react";
import { Link } from "react-router-dom";
import { FaTimes, FaStar, FaThumbsUp, FaUser } from "react-icons/fa";

import getImageUrl from "../../utils/imageUtils";
import ProgressBar from "../common/ProgressBar";
import styles from "../../styles/pages/product/ProductDetailsPage.module.css";

const ProductReviewsModal = ({
  isOpen,
  reviews,
  averageRating,
  ratingDistribution,
  approvedCount,
  user,
  onClose,
  onHelpful,
  onLoginRequired,
}) => {
  if (!isOpen) return null;
  const publicCount = approvedCount ?? reviews.filter((review) => review.approved).length;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.reviewsModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>All Reviews ({publicCount})</h3>
          <button
            className={styles.modalCloseBtn}
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.modalRatingSummary}>
            <div className={styles.averageRating}>
              <span className={styles.ratingValue}>{averageRating.toFixed(1)}</span>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar
                    key={star}
                    className={
                      star <= Math.round(averageRating)
                        ? styles.starFilled
                        : styles.starEmpty
                    }
                    size={20}
                  />
                ))}
              </div>
              <span className={styles.reviewCount}>
                ({publicCount} {publicCount === 1 ? "review" : "reviews"})
              </span>
            </div>
          </div>

          {reviews.length > 0 && (
            <div className={styles.modalRatingDistribution}>
              <h4 className={styles.distributionTitle}>Rating Breakdown</h4>
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = ratingDistribution[rating] || 0;
                const percentage = publicCount > 0 ? (count / publicCount) * 100 : 0;
                return (
                  <div key={rating} className={styles.ratingBarRow}>
                    <span className={styles.ratingLabel}>
                      {rating} <FaStar className={styles.starFilled} size={12} />
                    </span>
                    <div className={styles.ratingBar}>
                      <ProgressBar
                        className={styles.ratingBarFill}
                        percentage={percentage}
                      />
                    </div>
                    <span className={styles.ratingCount}>{count}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.modalReviewsList}>
            {reviews.length === 0 ? (
              <p>No reviews to display</p>
            ) : (
              reviews.map((review) => (
                <div key={review._id} className={styles.modalReviewItem}>
                  <div className={styles.reviewHeader}>
                    <div className={styles.reviewUserInfo}>
                      {review.user?.avatar ? (
                        <img
                          src={getImageUrl(review.user.avatar)}
                          alt="User avatar"
                          className={styles.reviewAvatar}
                        />
                      ) : (
                        <div className={styles.reviewAvatarPlaceholder}>
                          <FaUser />
                        </div>
                      )}
                      <div className={styles.reviewUser}>
                        {review.user?._id || review.user ? (
                          <Link
                            to={`/user/${review.user._id || review.user}`}
                            className={styles.reviewAuthorLink}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <strong>
                              {review.user?.username ||
                              (review.user?.firstName && review.user?.lastName)
                                ? `${review.user.firstName} ${review.user.lastName}`
                                : review.user?.email?.split("@")[0] || "Anonymous"}
                            </strong>
                          </Link>
                        ) : (
                          <strong>
                            {review.user?.username ||
                            (review.user?.firstName && review.user?.lastName)
                              ? `${review.user.firstName} ${review.user.lastName}`
                              : review.user?.email?.split("@")[0] || "Anonymous"}
                          </strong>
                        )}
                        <div className={styles.reviewStars}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              className={star <= review.rating ? styles.starFilled : styles.starEmpty}
                              size={14}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className={styles.reviewMetaActions}>
                      <span className={styles.reviewDate}>
                        {new Date(review.createdAt).toLocaleDateString()}
                      </span>
                      {review.approved === false && (
                        <span className={styles.pendingBadge}>Pending approval</span>
                      )}
                      {review.helpful !== undefined && review.approved !== false && (
                        <button
                          className={styles.helpfulBtn}
                          onClick={() => (user ? onHelpful(review._id) : onLoginRequired())}
                          type="button"
                        >
                          <FaThumbsUp /> Helpful ({review.helpful || 0})
                        </button>
                      )}
                    </div>
                  </div>
                  <p className={styles.reviewComment}>{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductReviewsModal;
